"use client"

// app/global-error.tsx
// Catches errors thrown in app/layout.tsx itself — the only boundary
// above the root layout. When this boundary is hit, Next.js unmounts
// the root layout entirely, so this file MUST provide its own
// <html> and <body> tags.
//
// Transient errors (stale bundle after deploy, hydration races) are
// common here and resolve on a second render. We auto-call reset()
// once after a short delay to silently recover from those. If the
// error re-throws after retrying, we stop retrying and show the
// branded error page so the user can retry manually or navigate away.
import { useEffect, useRef } from "react"

interface GlobalErrorProps {
  error: Error & { digest?: string }
  reset: () => void
}

export default function GlobalError({ error, reset }: GlobalErrorProps) {
  const hasRetried = useRef(false)

  useEffect(() => {
    console.error("Global error boundary caught:", error)

    if (!hasRetried.current) {
      hasRetried.current = true
      const timer = setTimeout(() => reset(), 500)
      return () => clearTimeout(timer)
    }
  }, [error, reset])

  return (
    <html lang="en">
      <body style={{ margin: 0, fontFamily: "system-ui, -apple-system, sans-serif", backgroundColor: "#f8f7f5" }}>
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "2rem",
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "28rem",
              padding: "2rem",
              borderRadius: "0.5rem",
              backgroundColor: "#ffffff",
              border: "1px solid #e5e4e1",
              boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1)",
              display: "flex",
              flexDirection: "column",
              gap: "1.5rem",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: "2.5rem",
                height: "2.5rem",
                borderRadius: "0.375rem",
                backgroundColor: "#fee2e2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span style={{ color: "#dc2626", fontWeight: 700, fontSize: "1.125rem" }}>!</span>
            </div>

            <div>
              <h1 style={{ fontSize: "1.25rem", fontWeight: 600, margin: 0 }}>Something went wrong</h1>
              <p style={{ fontSize: "0.875rem", color: "#6b7280", marginTop: "0.5rem", lineHeight: 1.5 }}>
                We encountered an unexpected error. Please try again.
              </p>
              {error.digest && (
                <p
                  style={{
                    fontSize: "0.75rem",
                    color: "#9ca3af",
                    marginTop: "0.5rem",
                    fontFamily: "ui-monospace, monospace",
                  }}
                >
                  Error ID: {error.digest}
                </p>
              )}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", width: "100%" }}>
              <button
                onClick={() => {
                  hasRetried.current = false
                  reset()
                }}
                style={{
                  width: "100%",
                  padding: "0.625rem 1rem",
                  borderRadius: "0.375rem",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  backgroundColor: "#1f2937",
                  color: "#ffffff",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                Try again
              </button>
              <a
                href="/dashboard"
                style={{
                  width: "100%",
                  padding: "0.625rem 1rem",
                  borderRadius: "0.375rem",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  backgroundColor: "#f8f7f5",
                  color: "#1f2937",
                  border: "1px solid #e5e4e1",
                  textAlign: "center",
                  textDecoration: "none",
                  boxSizing: "border-box",
                }}
              >
                Back to dashboard
              </a>
            </div>
          </div>
        </div>
      </body>
    </html>
  )
}
